import React, { useState } from "react";
import api from "@/lib/api";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import GstVaultDialog, { VaultBadge } from "@/components/clients/GstVaultDialog";
import { openExternal, copyText } from "@/lib/native";

export const isGstTask = (t) => t?.specialization === "gst" || /\bgst\b|gstr/i.test(t?.service || "");

export const copy = async (text, what) => {
  if (await copyText(text)) toast.success(`${what} copied`);
  else window.prompt(`Copy the ${what.toLowerCase()}:`, text);
};

export async function launchPortal(clientId, onDone) {
  const { data } = await api.get(`/gst-vault/${clientId}`);
  await copy(data.username, "Username");
  const opened = await openExternal(data.portal_url);
  toast.message(data.status === "expired" ? "Password expired — update it after login" : "Portal opened", {
    description: "Username is on your clipboard. Paste it, then copy the password.",
    action: { label: "Copy password", onClick: () => copy(data.password, "Password") },
    duration: 12000,
  });
  if (!opened) toast.error("Popup blocked — allow popups for this site");
  api.post(`/gst-vault/${clientId}/opened`).then(() => onDone?.()).catch(() => {});
  return data;
}

/** Badge + one-tap launch for a task's client. `vault` = /gst-vault/status payload. */
export default function GstPortalLaunch({ task, vault, isAdmin, onChanged, compact, tid }) {
  const [open, setOpen] = useState(false);
  if (!vault?.allowed || !isGstTask(task) || !task.client_id) return null;
  const item = vault.items?.[task.client_id];
  const status = item?.status || "missing";
  const client = { id: task.client_id, name: task.client_name };

  const go = async () => {
    if (status === "missing") { setOpen(true); return; }
    try { await launchPortal(task.client_id, onChanged); }
    catch (e) { if (e.response?.status === 404) setOpen(true); else toast.error("Could not open portal", { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <>
      <span className={`inline-flex items-center gap-1 whitespace-nowrap ${compact ? "" : "mt-1"}`}>
        <button data-testid={`${tid}-badge`} onClick={() => setOpen(true)} title="GST portal login details" className="hover:opacity-80">
          <VaultBadge status={status} daysLeft={item?.days_left} tid={`${tid}-status`} />
        </button>
        <button data-testid={`${tid}-open`} onClick={go} title={status === "missing" ? "Add GST portal login" : "Open GST portal · copies username"}
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border transition-colors ${status === "missing" ? "border-zinc-300 text-zinc-600 hover:bg-zinc-100" : "bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-700"}`}>
          <ExternalLink size={10} /> {status === "missing" ? "Add login" : "File now"}
        </button>
      </span>
      <GstVaultDialog client={client} open={open} onOpenChange={setOpen} isAdmin={isAdmin} onChanged={onChanged} />
    </>
  );
}
